import React, { useEffect, useMemo, useState, useRef } from "react";
import { apiRequest } from "../utils/api";
import Holidays from "date-holidays";
import ScheduleDayModal from "./ScheduleDayModal";
import "../styles/SchedulePanel.css";

function formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getMonthRange(baseDate) {
  const year = baseDate.getFullYear();
  const month = baseDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  return {
    start: formatDate(firstDay),
    end: formatDate(lastDay),
  };
}

function buildCalendarDays(baseDate) {
  const year = baseDate.getFullYear();
  const month = baseDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const startWeekday = firstDay.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < startWeekday; i++) {
    cells.push(null);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    cells.push(new Date(year, month, day));
  }
  return cells;
}

function SchedulePanel({ storeId }) {
  const [todayItems, setTodayItems] = useState([]);
  const [countdownItems, setCountdownItems] = useState([]);
  const [monthItems, setMonthItems] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showDoneToday, setShowDoneToday] = useState(false);
  const todayString = new Date().toISOString().slice(0, 10);
  const hd = new Holidays("JP");
  const [confirmItem, setConfirmItem] = useState(null);
  const [repeatNext, setRepeatNext] = useState(false);
  const [repeatDays, setRepeatDays] = useState(7);
  const [errorMessage, setErrorMessage] = useState("");

  const todayControllerRef = useRef(null);
  const countdownControllerRef = useRef(null);
  const monthControllerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (todayControllerRef.current) todayControllerRef.current.abort();
      if (countdownControllerRef.current) countdownControllerRef.current.abort();
      if (monthControllerRef.current) monthControllerRef.current.abort();
    };
  }, []);


  const holdTimerRef = useRef(null);

  const startHold = (direction) => {
    changeDays(direction);

    holdTimerRef.current = setInterval(() => {
      changeDays(direction);
    }, 120);
  };

  const stopHold = () => {
    if (holdTimerRef.current) {
      clearInterval(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  };

  const changeDays = (direction) => {
    setRepeatDays((prev) => {
      const num = Number(prev) || 1;
      if (direction === "plus") return num + 1;
      return Math.max(1, num - 1);
    });
  };

  const fetchTodayItems = async () => {
    if (todayControllerRef.current) {
      todayControllerRef.current.abort();
    }

    const controller = new AbortController();
    todayControllerRef.current = controller;

    try {
      const res = await apiRequest(
        `inventory/schedules/today/?store=${storeId}`,
        "GET",
        null,
        {},
        true,
        controller.signal
      );
      const items = Array.isArray(res) ? res : (res?.results || []);
      setTodayItems(items);
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("today fetch error:", error);
      setTodayItems([]);
    }
  };

  const fetchCountdownItems = async () => {
    if (countdownControllerRef.current) {
      countdownControllerRef.current.abort();
    }

    const controller = new AbortController();
    countdownControllerRef.current = controller;

    try {
      const res = await apiRequest(
        `inventory/schedules/countdown/?store=${storeId}`,
        "GET",
        null,
        {},
        true,
        controller.signal
      );
      const items = Array.isArray(res) ? res : (res?.results || []);
      setCountdownItems(items);
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("countdown fetch error:", error);
      setCountdownItems([]);
    }
  };

  const fetchMonthItems = async () => {
    if (monthControllerRef.current) {
      monthControllerRef.current.abort();
    }

    const controller = new AbortController();
    monthControllerRef.current = controller;

    try {
      const { start, end } = getMonthRange(currentMonth);

      const res = await apiRequest(
        `inventory/schedules/?start_date=${start}&end_date=${end}&store=${storeId}`,
        "GET",
        null,
        {},
        true,
        controller.signal
      );

      const items = Array.isArray(res) ? res : (res?.results || []);
      setMonthItems(items);
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("month fetch error:", error);
      setMonthItems([]);
    }
  };

  const fetchAll = async () => {
    await Promise.all([
      fetchTodayItems(),
      fetchCountdownItems(),
      fetchMonthItems(),
    ]);
  };

  useEffect(() => {
    if (!storeId) return;
    fetchAll();
  }, [storeId, currentMonth]);

  const visibleTodayItems = useMemo(() => {
    if (showDoneToday) return todayItems;
    return todayItems.filter((item) => !item.is_done);
  }, [todayItems, showDoneToday]);

  const todayActiveCount = useMemo(() => {
    return todayItems.filter((item) => !item.is_done).length;
  }, [todayItems]);

  const sortedCountdownItems = useMemo(() => {
    return [...countdownItems]
      .filter((item) => !item.is_done)
      .sort((a, b) => (a.countdown_days ?? 9999) - (b.countdown_days ?? 9999));
  }, [countdownItems]);

  const dateMap = useMemo(() => {
    const map = {};
    monthItems.forEach((item) => {
      const key = item.due_date || (item.start_datetime ? item.start_datetime.slice(0, 10) : null);
      if (!key) return;
      if (!map[key]) map[key] = [];
      map[key].push(item);
    });
    return map;
  }, [monthItems]);

  const calendarDays = useMemo(() => buildCalendarDays(currentMonth), [currentMonth]);

  const handleToggleDone = (item) => {
    setConfirmItem(item);
    setRepeatNext(false);
    setRepeatDays(7);
  };
  
  const handleConfirmToggleDone = async () => {
    if (!confirmItem) return;

    try {
      await apiRequest(`inventory/schedules/${confirmItem.id}/`, "PATCH", {
        is_done: !confirmItem.is_done,
      });

      if (!confirmItem.is_done && repeatNext) {
        if (!confirmItem.due_date) {
          throw new Error("期限日がないため、次回予定を追加できません。");
        }

        const baseDate = new Date(confirmItem.due_date);
        baseDate.setDate(baseDate.getDate() + Number(repeatDays || 7));

        const yyyy = baseDate.getFullYear();
        const mm = String(baseDate.getMonth() + 1).padStart(2, "0");
        const dd = String(baseDate.getDate()).padStart(2, "0");

        try {
          await apiRequest("inventory/schedules/", "POST", {
            title: confirmItem.title,
            description: confirmItem.description || "",
            category: confirmItem.category || null,
            store: confirmItem.store || null,
            due_date: `${yyyy}-${mm}-${dd}`,
            is_all_day: confirmItem.is_all_day ?? true,
            is_done: false,
            is_pinned: confirmItem.is_pinned ?? false,
            memo: confirmItem.memo || "",
          });
        } catch (postError) {
          throw new Error("処理済みへの更新は完了しましたが、次回予定の追加に失敗しました。");
        }
      }

      setConfirmItem(null);
      setRepeatNext(false);
      setRepeatDays(7);
      fetchAll();
    } catch (error) {
      console.error("toggle done error:", error);
      setErrorMessage(error.message || "更新に失敗しました。");
    }
  };

  const openDayModal = (date) => {
    setSelectedDate(formatDate(date));
    setIsModalOpen(true);
  };

  const moveMonth = (diff) => {
    setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + diff, 1));
  };

  const getCountdownLabel = (days) => {
    if (days == null) return "";
    if (days < 0) return `期限超過 ${Math.abs(days)}日`;
    if (days === 0) return "今日";
    if (days === 1) return "あと1日";
    return `あと${days}日`;
  };


  return (
    <>
      <div className="schedule-panel">
        <div className="schedule-panel-section">
          <div className="schedule-list">
            {sortedCountdownItems.length === 0 ? (
              <div className="schedule-empty">表示対象はありません</div>
            ) : (
              sortedCountdownItems.map((item) => (
                <div key={item.id} className="schedule-item countdown">
                  <div className="schedule-item-main">
                    <div className="schedule-title-row">
                      <strong>{item.title}</strong>
                      {item.category_name && (
                        <span className="schedule-badge">{item.category_name}</span>
                      )}
                    </div>
                    <div className="schedule-meta">
                      {item.due_date && <span>期限: {item.due_date}</span>}
                    </div>
                  </div>
                  <div className="schedule-countdown-value">
                    {getCountdownLabel(item.countdown_days)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        <div className="schedule-panel-sectionbox">
          <div className="schedule-panel-section-left">
            <div className="schedule-panel-header">
              <h2>
                本日の予定
                {todayActiveCount > 0 && (
                  <span className="count">{todayActiveCount}</span>
                )}
              </h2>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={showDoneToday}
                  onChange={(e) => setShowDoneToday(e.target.checked)}
                />

                <span className="toggle-slider"></span>

                <span className="toggle-label">
                  処理済みも表示
                </span>
              </label>
            </div>

            <div className="schedule-list-today">
              {visibleTodayItems.length === 0 ? (
                <div className="schedule-empty">本日の未処理予定はありません</div>
              ) : (
                visibleTodayItems.map((item) => {
                  const isOverdue =
                    item.due_date &&
                    item.due_date < todayString &&
                    !item.is_done;

                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={`schedule-item clickable ${item.is_done ? "is-done" : ""} ${isOverdue ? "is-overdue" : ""}`}
                      onClick={() => handleToggleDone(item)}
                    >
                      <div className="schedule-item-main">
                        <div className="schedule-title-row">
                          <strong>{item.title}</strong>
                          {item.category_name && (
                            <span className="schedule-badge">{item.category_name}</span>
                          )}
                          {item.memo && (
                            <span className="schedule-badge2">memo</span>
                          )}
                        </div>
                        <div className="schedule-meta">
                          {item.due_date && <span>期限: {item.due_date}</span>}
                          {item.start_datetime && <span>開始: {item.start_datetime.slice(11, 16)}</span>}
                        </div>
                      </div>
                      <div className="schedule-status-label">
                        {item.is_done ? "処理済み" : "未処理"}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
          <div className="schedule-panel-section-right">
            <div className="schedule-panel-header">
              <button type="button" onClick={() => moveMonth(-1)}>前月</button>
              <h2>
                {currentMonth.getFullYear()}年 {currentMonth.getMonth() + 1}月
              </h2>
              <button type="button" onClick={() => moveMonth(1)}>次月</button>
            </div>

            <div className="calendar-weekdays">
              {["日", "月", "火", "水", "木", "金", "土"].map((w) => (
                <div key={w} className="calendar-weekday">{w}</div>
              ))}
            </div>

            <div className="calendar-grid">
              {calendarDays.map((date, idx) => {
                if (!date) {
                  return <div key={`blank-${idx}`} className="calendar-cell blank" />;
                }

                const dateStr = formatDate(date);
                const items = dateMap[dateStr] || [];

                const countdownCount = items.filter(
                  (x) => !x.is_done && x.show_countdown
                ).length;

                const normalCount = items.filter(
                  (x) => !x.is_done && !x.show_countdown
                ).length;

                const activeCount = items.filter((x) => !x.is_done).length;
                const dayOfWeek = date.getDay();
                const isToday = dateStr === todayString;
                const holiday = hd.isHoliday(dateStr);
                console.log("calendar items", dateStr, items);

                return (
                  <button
                    type="button"
                    key={dateStr}
                    className={`calendar-cell
                      ${isToday ? "today" : ""}
                      ${holiday ? "holiday" : ""}
                      ${dayOfWeek === 0 ? "sun" : ""}
                      ${dayOfWeek === 6 ? "sat" : ""}
                    `}
                    onClick={() => openDayModal(date)}
                  >
                    <div className="calendar-day">{date.getDate()}</div>

                    {holiday && (
                      <div className="calendar-holiday-name">
                        {holiday[0].name}
                      </div>
                    )}
                    <div className="calendar-badge-container"> 
                      {normalCount > 0 && (
                        <div className="calendar-badge-count normal">
                          {normalCount}
                        </div>
                      )}
                      {countdownCount > 0 && (
                        <div className="calendar-badge-count countdown">
                          {countdownCount}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <ScheduleDayModal
          storeId={storeId}
          selectedDate={selectedDate}
          onClose={() => setIsModalOpen(false)}
          onSaved={fetchAll}
        />
      )}
      {confirmItem && (
        <div className="schedule-confirm-overlay" onClick={() => setConfirmItem(null)}>
          <div
            className="schedule-confirm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="schedule-confirm-title">
              {confirmItem.title}
            </div>

            {confirmItem.memo && (
              <div className="schedule-confirm-memo">
                {confirmItem.memo}
              </div>
            )}

            <div className="schedule-confirm-message">
              {confirmItem.is_done
                ? "この予定を未処理に戻しますか？"
                : "この予定を処理済みにしますか？"}
            </div>

            {!confirmItem.is_done && (
              <div className="schedule-repeat-box">
                <label className="schedule-repeat-check">
                  <input
                    type="checkbox"
                    checked={repeatNext}
                    onChange={(e) => setRepeatNext(e.target.checked)}
                  />
                  次回予定も追加する
                </label>

                {repeatNext && (
                  <div className="schedule-repeat-days-row">
                    <span>何日後に追加</span>
                    <div className="repeat-days-control">
                      <button
                        type="button"
                        className="repeat-days-btn"
                        onMouseDown={() => startHold("minus")}
                        onMouseUp={stopHold}
                        onMouseLeave={stopHold}
                        onTouchStart={() => startHold("minus")}
                        onTouchEnd={stopHold}
                      >
                        −
                      </button>

                      <input
                        type="number"
                        min="1"
                        value={repeatDays}
                        onChange={(e) => setRepeatDays(e.target.value)}
                      />

                      <button
                        type="button"
                        className="repeat-days-btn"
                        onMouseDown={() => startHold("plus")}
                        onMouseUp={stopHold}
                        onMouseLeave={stopHold}
                        onTouchStart={() => startHold("plus")}
                        onTouchEnd={stopHold}
                      >
                        +
                      </button>
                    </div>
                    <span>日後</span>
                  </div>
                )}
              </div>
            )}

            <div className="schedule-confirm-actions">
              <button
                type="button"
                className="confirm-ok"
                onClick={handleConfirmToggleDone}
              >
                {confirmItem.is_done ? "未処理に戻す" : "処理済みにする"}
              </button>

              <button
                type="button"
                className="confirm-cancel"
                onClick={() => {
                  setConfirmItem(null);
                  setRepeatNext(false);
                  setRepeatDays(7);
                }}
              >
                キャンセル
              </button>
            </div>
          </div>
        </div>
      )}
      {errorMessage && (
        <div className="schedule-confirm-overlay" onClick={() => setErrorMessage("")}>
          <div
            className="schedule-confirm-modal error"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="schedule-confirm-title">
              エラー
            </div>

            <div className="schedule-confirm-message">
              {errorMessage}
            </div>

            <div className="schedule-confirm-actions">
              <button
                type="button"
                className="confirm-ok"
                onClick={() => setErrorMessage("")}
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SchedulePanel;